// Shared types + pure helpers for the analysis-model registry and the
// model+slot run picker. Ported unchanged from
// docker/admin/src/lib/analysis-models.ts — kept free of React/DOM so it
// unit-tests in isolation.

export interface AnalysisModel {
  id: number;
  label: string;
  ai_model: string | null;
  ai_version: string | null;
  webhook_path: string;
  default_slot: number;
  sort_order: number;
  is_preferred: boolean;
  no_market: boolean;
  is_active: boolean;
  notes?: string | null;
}

/** The model slots a robot can run into or be swapped between. */
export const ANALYSIS_SLOTS = [1, 2, 3, 4] as const;

/** Auto review suggestions: written by the auto review database function, never by a robot run or a swap. */
export const AUTO_REVIEW_SLOT = 5;

/**
 * The slots staff can view: the model slots plus auto review. 9 (the swap buffer)
 * only holds rows inside a swap's transaction, so it is never shown.
 */
export const VIEWABLE_ANALYSIS_SLOTS = [...ANALYSIS_SLOTS, AUTO_REVIEW_SLOT] as const;

/**
 * Whether the swap bar can swap two slots: any two model slots, or T5 with T1. T5 ⇄ T1 applies the auto
 * review's suggestion: the server swaps each race's T1 with the slot the review put forward (T5 itself only
 * holds copies), so T5 never pairs with T2-T4.
 */
export function canSwapSlots(from: number, to: number): boolean {
  if (from === to) return false;
  if (from === AUTO_REVIEW_SLOT || to === AUTO_REVIEW_SLOT) return from === 1 || to === 1;
  return (ANALYSIS_SLOTS as readonly number[]).includes(from) && (ANALYSIS_SLOTS as readonly number[]).includes(to);
}

export function isViewableAnalysisSlot(typeId: number): boolean {
  return (VIEWABLE_ANALYSIS_SLOTS as readonly number[]).includes(Number(typeId));
}

/**
 * Derive the n8n origin (e.g. "https://n8n.tippingiq.com") from an existing
 * full webhook URL such as VITE_N8N_ANALYSIS_URL. Returns null if unparseable.
 * The model registry stores only the webhook path, so we reuse the configured
 * base to stay environment-portable (local vs prod).
 */
export function n8nOriginFromUrl(fullWebhookUrl: string | null | undefined): string | null {
  if (!fullWebhookUrl) return null;
  try {
    return new URL(fullWebhookUrl).origin;
  } catch {
    return null;
  }
}

/** Build the trigger URL for a model run: `${base}/webhook/${path}?<params>`. */
export function buildModelWebhookUrl(
  base: string,
  webhookPath: string,
  params: Record<string, string | number | undefined>
): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const cleanBase = base.replace(/\/+$/, '');
  const cleanPath = webhookPath.replace(/^\/+/, '');
  const query = qs.toString();
  return `${cleanBase}/webhook/${cleanPath}${query ? `?${query}` : ''}`;
}

/** Active models sorted by preference (sort_order, then label). */
export function sortedActiveModels(models: AnalysisModel[]): AnalysisModel[] {
  return models
    .filter((m) => m.is_active)
    .sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label));
}

/** The default model for the split-button body: the first preferred active
 *  model, else the first active model. */
export function defaultModel(models: AnalysisModel[]): AnalysisModel | null {
  const active = sortedActiveModels(models);
  return active.find((m) => m.is_preferred) ?? active[0] ?? null;
}

/** Short model caption, e.g. "Deepseek · Flash" — falls back to the label. */
export function modelCaption(m: Pick<AnalysisModel, 'ai_model' | 'ai_version' | 'label'>): string {
  const parts = [m.ai_model, m.ai_version].filter(Boolean);
  return parts.length ? parts.join(' · ') : m.label;
}
